import { openDataDatabase, type DataDatabase } from './data-db'
import { knownScopeKeyOf, type ScopeSource } from './team-scope'

/**
 * **Quelle base de tournée est ouverte** — la seule question dont dépend le cloisonnement de la
 * tablette partagée (D22 pt.4).
 *
 * Il y a une base par équipe (`ghp-terrain-data-<équipe>`), et ce porteur en tient **une seule
 * ouverte** : celle de la session en cours. Une équipe ne peut pas lire les données d'une autre
 * parce que sa base n'est pas ouverte — pas parce qu'un filtre l'en empêche. Il n'y a donc rien
 * à estampiller à l'écriture, rien à filtrer à la lecture, et aucune de ces deux disciplines à
 * tenir sur chaque nouvelle requête.
 *
 * ⚠️ **Le vidage du cache de lectures fait partie du dispositif**, et c'est la seule pièce que
 * les bases ne portent pas : le cache TanStack vit en mémoire, ne périme jamais
 * (`staleTime: Infinity`, `app/query-client.ts`) et le départ d'un agent **navigue** sans
 * recharger la page. Sans `onTeamChange`, l'équipe entrante se ferait servir depuis le cache ce
 * que la sortante y avait chargé, sans qu'aucune base ne soit relue.
 */
export interface TeamDataStore {
  /**
   * La base de l'équipe en cours, `null` tant qu'aucune session n'a nommé d'équipe.
   *
   * ⚠️ Stable tant que l'équipe ne change pas : c'est ce qui permet de s'y abonner depuis un
   * écran (`app/use-team-data.ts`) sans rebâtir quoi que ce soit à chaque rafraîchissement de
   * jeton (M23).
   */
  current(): DataDatabase | null
  /** Prévient d'un **changement de base**, jamais d'un changement de session. */
  subscribe(listener: () => void): () => void
  /** Ferme ce qui est ouvert et cesse de suivre la session (démontage, tests). */
  close(): void
}

/**
 * Ouvre, referme, et prévient — le tout à partir de l'équipe que la session déclare.
 *
 * ⚠️ **Une équipe inconnue ne referme rien.** Une déconnexion rend `unknown`, et ce n'est pas
 * l'annonce d'une autre équipe : c'est l'absence d'annonce. Refermer là ne protégerait de rien —
 * l'équipe entrante ouvre *sa* base en entrant, et les écrans de la sortante ne sont plus
 * montés — mais coûterait deux choses réelles : une passe d'envoi en cours perdrait sa base sous
 * elle au milieu d'un téléversement, et l'écran qui se démonte lèverait au lieu de s'effacer.
 */
export function createTeamDataStore(
  source: ScopeSource,
  options: { onTeamChange?: () => void } = {},
): TeamDataStore {
  const listeners = new Set<() => void>()
  let open: DataDatabase | null = null
  let openKey: string | null = null

  function follow(): void {
    const scopeKey = knownScopeKeyOf(source.get())
    if (scopeKey === null || scopeKey === openKey) return

    const previous = open
    openKey = scopeKey

    open = openDataDatabase(scopeKey)

    if (previous !== null) {
      // Refermée **après** l'ouverture de la suivante : entre les deux, `current()` ne doit
      // jamais rendre une base fermée à un écran qui se rafraîchit.
      previous.close()

      // ⚠️ **Une première ouverture ne jette rien** : il n'y a pas de base précédente, donc rien
      // en cache qui vienne d'une autre équipe. Vider là ferait tomber, au démarrage, les
      // lectures qu'un écran vient de lancer.
      options.onTeamChange?.()
    }

    // Les abonnés, eux, sont prévenus **aussi** de la première ouverture : c'est elle qui donne
    // une base à observer à `watchLocalData`, démarré avant que la session ne soit lue.
    for (const listener of listeners) listener()
  }

  const unfollow = source.subscribe(follow)
  follow()

  return {
    current: () => open,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    close() {
      unfollow()
      open?.close()
      open = null
      openKey = null
      listeners.clear()
    },
  }
}
